import { FieldPolicy } from '@apollo/client';
import { isNullish } from '@apollo/client/cache/inmemory/helpers';
import { formatDateYYYYMMDD } from './localDateLink';

// Type Policy for a LocalDate custom scalar, transmitted as a string in 'YYYY-MM-DD' format, representing a date in local time
export const localDateTypePolicy: FieldPolicy<string, string | Date, Date> = {
  read: (existing) => {
    if (isNullish(existing)) return existing;
    // If for some reason it's already a Date (e.g. from optimistic response not yet normalized?), handle it.
    if (isDate(existing)) return existing;

    if (typeof existing === 'string') {
      // Handle ISO strings or YYYY-MM-DD
      const datePart = existing.split('T')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        const [year, month, day] = datePart.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
    }
    // If it's not a string or Date, return as is (shouldn't happen if types are correct)
    return existing as unknown as Date;
  },
  merge: (_, incoming) => {
    if (isNullish(incoming)) return incoming;

    if (isDate(incoming)) {
      return formatDateYYYYMMDD(incoming);
    }

    if (typeof incoming === 'string') {
      // Normalize ISO strings to YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}T/.test(incoming)) {
        return incoming.split('T')[0];
      }
      // Validate YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(incoming)) {
        return incoming;
      }
      throw new Error(`Invalid date format: ${incoming}`);
    }

    return incoming as unknown as string;
  },
};

const isDate = (value: any): value is Readonly<Date> => {
  return value instanceof Date && !isNaN(value.getTime());
};
